import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo } from 'react';
import { AGENT_TEAM_MEMBER_FILTER } from '@/common/constants/identityFilters';
import type { IdentityRequest } from '$api-clients/hooks';
import { useIdentity } from '$api-clients/hooks';

type UserInfoTeam = NonNullable<
  IdentityRequest<'/auth/userinfo'>['Data']['teams']
>[number];
type TeamMember = NonNullable<
  IdentityRequest<'/users/{userId}/interaction-permissions/{type}/team-members'>['Data']['data']
>[number];

const teamMembersInit = (userId: string, type: 'submit-as' | 'sharing') => ({
  params: {
    path: { userId, type },
    query: { limit: 500, q: AGENT_TEAM_MEMBER_FILTER },
  },
});

/**
 * Declarative SWR replacement for the previous effect-driven implementation.
 *
 * Previously this fired three `identityApi.GET` calls (userinfo + sharing +
 * submit-as team-members) imperatively inside a `useEffect`, bypassing SWR's
 * cache and deduping. Across its 5+ consumers that re-issued the requests on
 * every mount/refetch. Routing through `useIdentity` shares one cache entry
 * per key (the `/auth/userinfo` key is shared with `useHasAccessToFeature`),
 * so the same data is fetched once and reused.
 */
export const useUserPermissionStates = ({ userId }: { userId?: string }) => {
  const {
    data: userInfo,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useIdentity('/auth/userinfo', userId ? undefined : null);

  const {
    data: sharingResponse,
    isLoading: sharingLoading,
    error: sharingError,
  } = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    userId ? teamMembersInit(userId, 'sharing') : null,
  );

  const {
    data: submitAsResponse,
    isLoading: submitAsLoading,
    error: submitAsError,
  } = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    userId ? teamMembersInit(userId, 'submit-as') : null,
  );

  // Preserve the observability the imperative version had: it reported fetch
  // failures to Datadog. SWR surfaces them as `error` rather than throwing.
  useEffect(() => {
    const error = userInfoError ?? sharingError ?? submitAsError;
    if (error !== undefined) {
      datadogRum.addError(error, {
        userId,
        message: 'Error fetching user permission states',
      });
    }
  }, [userInfoError, sharingError, submitAsError, userId]);

  const { submitAsStates, sharingStates } = useMemo<{
    submitAsStates: string[];
    sharingStates: string[];
  }>(() => {
    if (!userId) {
      return { submitAsStates: [], sharingStates: [] };
    }

    // `market`/`state` are state-code unions in the identity schema, so narrow
    // on the element's own non-nullable type rather than `string` (a `string`
    // predicate isn't assignable to the union). The resulting state-code values
    // are still assignable to the `string[]` public return type.
    const notNullish = <T>(value: T): value is NonNullable<T> => value != null;

    const activeTeamIds = new Set(
      Object.keys(userInfo?.accessibleTeams?.nodes ?? {}),
    );
    const teams: UserInfoTeam[] = userInfo?.teams ?? [];
    const currentUserStates = teams
      .filter((team) => activeTeamIds.has(team.id))
      .map((team) => team.market)
      .filter(notNullish);

    const submitAsMembers: TeamMember[] = submitAsResponse?.data ?? [];
    const sharingMembers: TeamMember[] = sharingResponse?.data ?? [];
    const submitAsLicenseStates = submitAsMembers
      .map((teamMember) => teamMember.license?.state)
      .filter(notNullish);
    const sharingLicenseStates = sharingMembers
      .map((teamMember) => teamMember.license?.state)
      .filter(notNullish);

    return {
      submitAsStates: [
        ...new Set([...submitAsLicenseStates, ...currentUserStates]),
      ].sort(),
      sharingStates: [
        ...new Set([...sharingLicenseStates, ...currentUserStates]),
      ].sort(),
    };
  }, [userId, userInfo, submitAsResponse, sharingResponse]);

  const allStates = useMemo(
    () => [...new Set([...submitAsStates, ...sharingStates])].sort(),
    [submitAsStates, sharingStates],
  );

  const loading = userId
    ? userInfoLoading || sharingLoading || submitAsLoading
    : false;

  return {
    submitAsStates,
    sharingStates,
    allStates,
    loading,
  };
};
